/**
 * Reusable Badge Component
 * For displaying status labels, roles, and tags
 */

type BadgeVariant = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral';
type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  className?: string;
  onClick?: () => void;
}

const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  primary: 'bg-accent-blue/10 text-accent-blue border-accent-blue/30',
  success: 'bg-green-500/10 text-green-500 border-green-500/30',
  warning: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30',
  danger: 'bg-red-500/10 text-red-500 border-red-500/30',
  info: 'bg-blue-400/10 text-blue-400 border-blue-400/30',
  neutral: 'bg-dark-bg/50 text-dark-text-muted border-dark-border/50',
};

const SIZE_CLASSES: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-3 py-1 text-sm',
  lg: 'px-4 py-1.5 text-base',
};

export default function Badge({
  children,
  variant = 'neutral',
  size = 'md',
  className = '',
  onClick,
}: BadgeProps) {
  const baseClasses = 'inline-flex items-center justify-center font-medium border rounded-full transition-colors';
  const variantClasses = VARIANT_CLASSES[variant];
  const sizeClasses = SIZE_CLASSES[size];
  const interactiveClasses = onClick ? 'cursor-pointer hover:opacity-80 active:opacity-60' : '';

  return (
    <span
      className={`${baseClasses} ${variantClasses} ${sizeClasses} ${interactiveClasses} ${className}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
    >
      {children}
    </span>
  );
}
